import React from "react";
import type { StatsDateRange, StatsGranularity } from "../../../api/stats/Stats.typed";
import {
  STATS_FIRST_DATE,
  formatPeriodLabel,
  periodBounds,
  shiftPeriod,
  toDateRange,
} from "../statsPeriodHelpers";
import type { PeriodBounds } from "../statsPeriodHelpers";

export type StatsPeriodState = {
  granularity: StatsGranularity;
  bounds: PeriodBounds;
  range: StatsDateRange;
  label: string;
  canGoPrev: boolean;
  canGoNext: boolean;
  setGranularity: (g: StatsGranularity) => void;
  goPrev: () => void;
  goNext: () => void;
};

export function useStatsPeriod(initial: StatsGranularity = "month"): StatsPeriodState {
  const [granularity, setGranularity] = React.useState<StatsGranularity>(initial);
  const [anchor, setAnchor] = React.useState<Date>(() => new Date());

  const bounds = React.useMemo(() => periodBounds(granularity, anchor), [granularity, anchor]);
  const { start: rangeStart, end: rangeEnd } = React.useMemo(() => toDateRange(bounds), [bounds]);
  const range = React.useMemo(() => ({ start: rangeStart, end: rangeEnd }), [rangeStart, rangeEnd]);

  const canGoPrev = bounds.start > STATS_FIRST_DATE;
  const canGoNext = bounds.end < new Date();

  const goPrev = React.useCallback(() => {
    setAnchor((prev) => shiftPeriod(granularity, prev, -1));
  }, [granularity]);

  const goNext = React.useCallback(() => {
    setAnchor((prev) => shiftPeriod(granularity, prev, 1));
  }, [granularity]);

  return {
    granularity,
    bounds,
    range,
    label: formatPeriodLabel(granularity, bounds.start),
    canGoPrev,
    canGoNext,
    setGranularity,
    goPrev,
    goNext,
  };
}
